import Cookies from 'js-cookie'
import locator from '@/service-locator'
import config from '@/config'
import router from '@/router'
import {
  getSalesforceProvider,
  updateSalesforceLocale,
} from '@/utils/salesforce'
import * as Native from '@/utils/native-bridge'

const SUPPORTED_SALESFORCE_LANG = ['en', 'ar']

const MAPPING_SALESFORCE_LOCALE = {
  en: 'en',
  ar: 'ar',
}

class SalesforceChatbotService {
  constructor() {
    this.metaFields = {}
    this.isInitialized = false
    this.provider = getSalesforceProvider()
    this.salesforceIframeOriginalStyle = undefined
  }

  isChatbotRoute = () => {
    return router.currentRoute?.name === 'chatbot'
  }

  isAvailable = () => {
    const {
      salesforceOrgId,
      salesforceDeploymentId,
      salesforceUnauthenticatedDeploymentId,
      salesforceEmbeddedServiceUrl,
      salesforceUnauthenticatedEmbeddedServiceUrl,
      salesforceScrt2Url,
      salesforcePartnerChanel,
    } = config

    return !!(
      salesforceOrgId &&
      salesforceDeploymentId &&
      salesforceUnauthenticatedDeploymentId &&
      salesforceEmbeddedServiceUrl &&
      salesforceUnauthenticatedEmbeddedServiceUrl &&
      salesforceScrt2Url &&
      salesforcePartnerChanel
    )
  }

  /**
   * @param targetLanguage
   * @returns {boolean}
   */
  changeChatBotLanguage = (targetLanguage) => {
    if (!SUPPORTED_SALESFORCE_LANG.includes(targetLanguage)) {
      return false
    }

    if (!this.metaFields) {
      this.metaFields = {}
    }

    const mappedLocale = MAPPING_SALESFORCE_LOCALE[targetLanguage]
    this.metaFields.locale = mappedLocale

    // Use the utility to update locale
    const locale = mappedLocale === 'ar' ? 'ar_SA' : 'en_US'
    updateSalesforceLocale(locale)

    return true
  }

  hideChatbot = async () => {
    try {
      const chatButton = document.querySelector(
        '.embeddedServiceHelpButton, [embeddedService-chatHeader-buttons]'
      )
      if (chatButton) {
        chatButton.style.display = 'none'
      }
    } catch (error) {
      // hide chatbot failed silently
    }
  }

  showChatbot = async () => {
    try {
      const chatButton = document.querySelector(
        '.embeddedServiceHelpButton, [embeddedService-chatHeader-buttons]'
      )
      if (chatButton) {
        chatButton.style.display = ''
      }
    } catch (error) {
      // show chatbot failed silently
    }
  }

  openChatbot = async () => {
    await this.provider.open()
  }

  closeChatbot = () => {
    this.provider.close()
  }

  _userPlatformChannel = () => {
    const { salesforcePartnerChanel } = config
    const userPlatform = Cookies.get('tamara_partner_platform') || ''

    if (userPlatform === 'ios') {
      return 'Chat (Partner iOS App)'
    } else if (userPlatform === 'android') {
      return 'Chat (Partner Android App)'
    }

    return salesforcePartnerChanel || 'Chat (Partner Web App)'
  }

  setChatMetaFields = async (newFields) => {
    if (!this.metaFields) {
      this.metaFields = {}
    }

    const { salesforcePartnerChanel } = config

    const locale =
      MAPPING_SALESFORCE_LOCALE[newFields?.language] ||
      this.metaFields.locale ||
      'en'

    const authStatus =
      newFields?.merchant_authenticated_status ??
      this.metaFields.merchant_authenticated_status ??
      false
        ? 'Yes'
        : 'No'

    const merchantEmail =
      newFields?.merchant_email || this.metaFields?.merchant_email_address || ''
    const merchantId =
      newFields?.merchant_id || this.metaFields?.merchant_id || ''
    const userId = newFields?.user_id || this.metaFields?.user_id || merchantId
    const firstName = newFields?.first_name || this.metaFields?.first_name || ''
    const merchantName =
      newFields?.merchant_name || this.metaFields?.merchant_name || ''

    const channel = this._userPlatformChannel() || salesforcePartnerChanel

    this.metaFields = {
      locale: locale,
      first_name: firstName,
      merchant_authenticated_status: authStatus,
      merchant_email_address: merchantEmail,
      user_id: userId,
      merchant_id: merchantId,
      channel,
      merchant_name: merchantName,
    }
  }

  /**
   * @param language
   * @param metaFields
   * @param sensitiveMetaFields
   */
  initChatBot = async ({ language, metaFields, sensitiveMetaFields }) => {
    if (!this.isAvailable()) {
      return
    }

    const targetLocale = MAPPING_SALESFORCE_LOCALE[language] || 'en'
    const locale = targetLocale === 'ar' ? 'ar_SA' : 'en_US'

    const prechatData = {
      Locale: targetLocale || 'en',
      User_ID: this.metaFields?.user_id || '',
      Merchant_ID: this.metaFields?.merchant_id || '',
      Email_Address: this.metaFields?.merchant_email_address || '',
      Channel: this.metaFields?.channel || 'Chat (Partner Web App)',
      Authenticated_Contact:
        this.metaFields?.merchant_authenticated_status || 'No',
      First_Name: this.metaFields?.first_name || '',
      Merchant_Name: this.metaFields?.merchant_name || '',
    }
    await this.provider.initialize({
      locale,
      countryCode: metaFields?.countryCode,
      customerId: metaFields?.merchant_id,
      prechatData,
      onLoad: () => {
        this.isInitialized = true
        setTimeout(() => {
          locator.store.dispatch(
            'chatbot/setSalesforceScriptLoadedStatus',
            true
          )
        }, 2000)

        if (this.isChatbotRoute()) {
          this.applySalesforceFullscreenStyles()
          setTimeout(() => {
            this.provider.open()
          }, 500)
        }
      },
      onError: () => {
        console.error('[SF Chat] Failed to load Embedded Messaging')
      },
      onClose: () => {
        if (this.isChatbotRoute()) {
          Native.submitMessage('back', { source: 'chatbot_screen' })
        }
      },
      onLinkClick: (link) => {
        if (this.isChatbotRoute()) {
          this.handleSalesforceLinkClick(link)
        }
      },
      onMaximize: () => {
        if (this.isChatbotRoute()) {
          this.handleSalesforceMaximize()
        }
      },
    })
  }

  cleanup = () => {
    this.provider.cleanup()
    this.isInitialized = false
    this.metaFields = {}
  }

  handleSalesforceLinkClick = (link) => {
    if (!link) {
      return
    }

    Native.submitMessage('link', {
      source: 'chatbot_screen',
      link: link,
    })
  }

  handleSalesforceMaximize = () => {
    this.applySalesforceFullscreenStyles()
  }

  applySalesforceFullscreenStyles = () => {
    const iframe = document.getElementById('embeddedMessagingFrame')
    if (!iframe) {
      return
    }
    if (typeof this.salesforceIframeOriginalStyle === 'undefined') {
      this.salesforceIframeOriginalStyle = iframe.getAttribute('style')
    }

    iframe.style.setProperty('min-width', '100vw', 'important')
    iframe.style.setProperty('min-height', '100vh', 'important')
    iframe.style.setProperty('width', '100vw', 'important')
    iframe.style.setProperty('height', '100vh', 'important')
    iframe.style.setProperty('padding', '0', 'important')
    iframe.style.setProperty('margin', '0', 'important')
    iframe.style.setProperty('background-color', 'transparent', 'important')
    iframe.style.setProperty('border-radius', '0', 'important')
    iframe.style.setProperty('border', 'none', 'important')
  }

  resetSalesforceFullscreenStyles = () => {
    if (typeof this.salesforceIframeOriginalStyle === 'undefined') {
      return
    }
    const iframe = document.getElementById('embeddedMessagingFrame')
    if (iframe) {
      this.restoreSalesforceIframeStyle(iframe)
    }
    this.salesforceIframeOriginalStyle = undefined
  }

  restoreSalesforceIframeStyle = (iframe) => {
    if (this.salesforceIframeOriginalStyle === null) {
      iframe.removeAttribute('style')
    } else {
      iframe.setAttribute('style', this.salesforceIframeOriginalStyle)
    }
  }
}

export default SalesforceChatbotService
